import type { Components, Theme } from "@mui/material";
import { alpha } from "@mui/material";

export const chipTheme: Components<Theme>["MuiChip"] = {
  defaultProps: {
    size: "small",
    variant: "filled",
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.borderRadius.xs,
      lineHeight: 1,
      fontWeight: 400,
      // Icon-only chip styles (when label is empty)
      "&:has(.MuiChip-label:empty)": {
        "& .MuiChip-label": {
          display: "none",
        },
        "& .MuiChip-icon": {
          marginLeft: 6,
          marginRight: 6,
        },
      },
      ".MuiChip-deleteIcon": {
        marginRight: "6px",
      },
    }),
    sizeSmall: ({ theme }) => ({
      fontSize: theme.typography.body3.fontSize,
      height: "24px",
      lineHeight: "24px",
    }),
    labelSmall: () => ({
      padding: "0 8px",
    }),
    sizeMedium: ({ theme }) => ({
      fontSize: theme.typography.body2.fontSize,
      height: "28px",
      lineHeight: "28px",
    }),
    labelMedium: () => ({
      padding: "2px 10px",
    }),
    filled: ({ theme }) => ({
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.text.main,
    }),
    filledDefault: ({ theme }) => ({
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.text.main,
      ".MuiChip-deleteIcon": {
        color: alpha(theme.palette.text.main, 0.7),
        "&:hover": {
          color: theme.palette.text.main,
        },
      },
    }),
    filledSuccess: ({ theme }) => ({
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
    }),
    filledError: ({ theme }) => ({
      backgroundColor: theme.palette.errorSecondary.light,
      color: theme.palette.errorSecondary.contrastText,
      ".MuiChip-deleteIcon": {
        color: alpha(theme.palette.errorSecondary.contrastText, 0.7),
        "&:hover": {
          color: theme.palette.errorSecondary.contrastText,
        },
      },
    }),
    filledWarning: ({ theme }) => ({
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
    }),
    filledInfo: ({ theme }) => ({
      backgroundColor: theme.palette.info.light,
      color: theme.palette.info.contrastText,
    }),
    filledAccent: ({ theme }) => ({
      backgroundColor: theme.palette.accent.light,
      color: theme.palette.accent.main,
      ".MuiChip-deleteIcon": {
        color: alpha(theme.palette.accent.main, 0.7),
        "&:hover": {
          color: theme.palette.accent.main,
        },
      },
    }),
    outlined: ({ theme }) => ({
      backgroundColor: theme.palette.secondary.light,
      borderColor: theme.palette.custom.borderHover,
    }),
    outlinedDefault: ({ theme }) => ({
      border: `1px solid ${theme.palette.custom.borderHover}`,
      backgroundColor: theme.palette.secondary.light,
      color: theme.palette.text.main,
      ".MuiChip-deleteIcon": {
        color: alpha(theme.palette.text.main, 0.7),
        "&:hover": {
          color: theme.palette.text.main,
        },
      },
    }),
    outlinedSuccess: ({ theme }) => ({
      border: `1px solid ${theme.palette.custom.borderSecondarySuccess}`,
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
    }),
    outlinedError: ({ theme }) => ({
      border: `1px solid ${theme.palette.custom.borderSecondaryError}`,
      backgroundColor: theme.palette.errorSecondary.light,
      color: theme.palette.errorSecondary.contrastText,
      ".MuiChip-deleteIcon": {
        color: alpha(theme.palette.errorSecondary.contrastText, 0.7),
        "&:hover": {
          color: theme.palette.errorSecondary.contrastText,
        },
      },
    }),
    outlinedWarning: ({ theme }) => ({
      border: `1px solid ${theme.palette.custom.borderSecondaryWarning}`,
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
    }),
    outlinedInfo: ({ theme }) => ({
      border: `1px solid ${theme.palette.custom.borderSecondaryInfo}`,
      backgroundColor: theme.palette.info.light,
      color: theme.palette.info.contrastText,
    }),
    outlinedAccent: ({ theme }) => ({
      border: `1px solid ${theme.palette.custom.borderAccent}`,
      backgroundColor: theme.palette.accent.light,
      color: theme.palette.accent.main,
    }),
    icon: () => ({
      marginLeft: "6px",
      marginRight: "-4px",
    }),
    iconSmall: () => ({
      width: "16px",
      height: "16px",
    }),
    iconMedium: () => ({
      width: "18px",
      height: "18px",
    }),
  },
};
